import { useEffect, useRef, useState } from 'react';

const VIDEO_1 = 'https://res.cloudinary.com/dd5pmshcy/video/upload/v1775246924/hf_20260403_200115_7ea89d60-d67b-4f00-a827-15b49d8a5b29_lcbend.mp4';
const VIDEO_2 = 'https://res.cloudinary.com/dd5pmshcy/video/upload/v1775246907/hf_20260403_193235_14c454bc-28f2-490d-848f-dd0a21224bd8_h2igtt.mp4';

const VALUES = [
  {
    icon: 'ri-scissors-cut-line',
    title: 'Precision Craftsmanship',
    desc: 'Every set is tailored with meticulous detail and care. No shortcuts. No compromises.',
  },
  {
    icon: 'ri-palette-line',
    title: 'Custom Design Experience',
    desc: 'Unique styles built entirely around your vision. Your nails, your identity.',
  },
  {
    icon: 'ri-award-line',
    title: 'Premium Products Only',
    desc: 'Long-lasting, high-quality gel finishes that look flawless from day one to week four.',
  },
];

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const [valuesVisible, setValuesVisible] = useState(false);
  const valuesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setValuesVisible(true); },
      { threshold: 0.2 }
    );
    if (valuesRef.current) observer.observe(valuesRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* ── Hero 1 ── */}
      <section id="hero" className="relative w-full h-screen min-h-[600px] overflow-hidden">
        <video
          src={VIDEO_1}
          autoPlay
          muted
          playsInline
          loop
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className="absolute inset-0 bg-black/20" />

        {/* Gold accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />

        {/* Content */}
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-6 text-center pt-20 md:pt-0">
          {/* Eyebrow */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            <span className="font-sans text-xs tracking-widest2 uppercase text-gold-light mb-5 block">
              Premium Nail Artistry
            </span>
          </div>

          {/* Headline */}
          <h1
            className={`font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white font-light leading-tight max-w-4xl transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
          >
            Refined Nail Art.
            <br />
            <em className="italic">Designed for You.</em>
          </h1>

          {/* Gold divider */}
          <div
            className={`w-16 h-px bg-gold my-6 md:my-8 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
          />

          {/* Subheadline */}
          <p
            className={`font-sans text-sm md:text-lg text-white/75 tracking-wider max-w-md transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            Custom gel nails. Precision. Luxury finish.
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 md:mt-10 transition-all duration-700 delay-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            <button
              onClick={() => window.open('https://calendly.com/lispinruiz/nailappointment', '_blank')}
              className="whitespace-nowrap font-sans text-sm tracking-widest uppercase px-8 md:px-10 py-3.5 md:py-4 bg-gold text-white hover:bg-gold-dark transition-all duration-300 cursor-pointer"
            >
              Book Appointment
            </button>
            <button
              onClick={() => scrollTo('gallery')}
              className="whitespace-nowrap font-sans text-sm tracking-widest uppercase px-8 md:px-10 py-3.5 md:py-4 border border-white/60 text-white hover:border-gold hover:text-gold transition-all duration-300 cursor-pointer"
            >
              View Designs
            </button>
            <button
              onClick={() => scrollTo('pricing')}
              className="whitespace-nowrap font-sans text-sm tracking-widest uppercase px-8 md:px-10 py-3.5 md:py-4 border border-white/60 text-white hover:border-gold hover:text-gold transition-all duration-300 cursor-pointer"
            >
              Pricing
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="font-sans text-xs tracking-widest uppercase text-white/40">Scroll</span>
          <i className="ri-arrow-down-line text-white/40 text-lg"></i>
        </div>
      </section>

      {/* ── Hero 2 + Value Proposition ── */}
      <section ref={valuesRef} className="relative w-full min-h-screen overflow-hidden py-24 md:py-32">
        {/* Background Video */}
        <video
          src={VIDEO_2}
          autoPlay
          muted
          playsInline
          loop
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        {/* Overlays for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />

        {/* Gold accent lines */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />

        {/* Content */}
        <div className="relative z-10 w-full px-6">
          <div className="max-w-6xl mx-auto">
            {/* Section label */}
            <div className={`text-center mb-16 transition-all duration-700 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <span className="font-sans text-xs tracking-widest2 uppercase text-gold-light block mb-4">
                Why Choose Us
              </span>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-light">
                The Studio Difference
              </h2>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {VALUES.map((item, i) => (
                <div
                  key={i}
                  className={`group flex flex-col items-center text-center p-8 md:p-10 bg-white/5 backdrop-blur-sm border border-white/20 hover:border-gold hover:bg-white/10 transition-all duration-500 cursor-default ${
                    valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${i * 150 + 200}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center mb-6">
                    <i className={`${item.icon} text-3xl text-gold group-hover:scale-110 transition-transform duration-300`}></i>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-white font-medium mb-4">
                    {item.title}
                  </h3>
                  <p className="font-sans text-sm text-white/70 leading-relaxed">
                    {item.desc}
                  </p>
                  <div className="w-8 h-px bg-gold mt-6 group-hover:w-16 transition-all duration-500" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}